import {
  createRouter,
  createMemoryHistory,
  createWebHistory,
} from 'vue-router';

import { scrollBehavior, layoutClient, Header, Sidebar, Navigation, NavigationBar } from '@ozdao/martyrs/modules/core/client';
import { storeAuth } from '@ozdao/martyrs/modules/auth/client';

import Logotype from '@/components/icons/Logotype.vue';
import HeaderAuth from '@/components/partials/HeaderAuth.vue';
// HeaderNavigation — обёртка-капсула (blur + border) поверх martyrs Navigation
// в горизонтальном режиме. Поддерживает item.dropdownComponent (см. HeaderMegaMenu).
import HeaderNavigation from '@/components/partials/HeaderNavigation.vue';
import SidebarNavigation from '@/components/partials/SidebarNavigation.vue';
import SidebarSupport from '@/components/partials/SidebarSupport.vue';
import { headerNavigationItems, sidebarNavigationItems } from '@/configs/navigation.config.js';
import { useAnalytics } from '@/composables/useAnalytics.js';
import configuratorRoutes from '@martyrs/src/design-system/configurator/router/configurator.router.js';

const isServer = typeof window === 'undefined';

let history = isServer ? createMemoryHistory() : createWebHistory();

const routes = [{
  path: '/:locale?',

  components: {
    default: layoutClient
  },

  name: 'Home',

  meta: {
    location: false,
    logotype: Logotype,
    header: Header,
    sidebar: Sidebar,
    sidebar_navigation: SidebarNavigation,
    sidebar_navigation_items: headerNavigationItems,
    navigationbar: NavigationBar,
    header_show_cart: false,
    header_show_profile: false,
    header_show_notifications: false,
    header_right_component: HeaderAuth,
    // Лендинг-стиль по умолчанию для всех дочерних роутов Home (включая community
    // и tools/*): свёрнутый сайдбар, overlay-хедер, лендинг-навигация.
    // Dashboard явно перекрывает это своим набором меты ниже.
    sidebar_width_hidden: 'w-zero',
    header_mode: 'overlay',
    header_navigation: HeaderNavigation,
    header_navigation_items: headerNavigationItems,
  },

  children: [
    {
      path: '',
      name: 'Home Page',
      meta: {
        title: { ru: 'Главная' },
        // Все лендинг-настройки лежат на родителе Home — наследуются сюда.
      },
      component: () => import('@/components/pages/Home.vue'),
    },
    // Стаб-страницы для пунктов мега-меню (заполнятся контентом отдельно).
    { path: 'tools/audit',     name: 'Tools Account Audit', meta: { title: { ru: 'Аудит Telegram-аккаунтов | Traffers' }, sidebar_width_hidden: 'w-zero' }, component: () => import('@/components/pages/ToolsAudit.vue') },
    { path: 'tools/templates', name: 'Tools Templates',     meta: { title: { ru: 'Шаблоны инвайтов и комментариев | Traffers' }, sidebar_width_hidden: 'w-zero' }, component: () => import('@/components/pages/ToolsTemplates.vue') },
    { path: 'tools/scripts',   name: 'Tools Scripts',       meta: { title: { ru: 'Готовые сценарии Telegram-задач | Traffers' }, sidebar_width_hidden: 'w-zero' }, component: () => import('@/components/pages/ToolsScripts.vue') },
    { path: 'tools/channels',  name: 'Tools Channels',      meta: { title: { ru: 'Каналы и чаты' }, sidebar_width_hidden: 'w-zero' }, component: () => import('@/components/pages/Soon.vue') },
    { path: 'about',                   name: 'About',                    meta: { title: { ru: 'О нас' }, sidebar_width_hidden: 'w-zero' }, component: () => import('@/components/pages/About.vue') },
    // Страница тарифов — пункт хедера «Тарифы» ведёт сюда, а не на якорь #pricing:
    // якорь работал только на лендингах, где есть SectionPricing.
    { path: 'pricing',                 name: 'Pricing',                  meta: { title: { ru: 'Тарифы и цены — автоматизация Telegram | Traffers' }, sidebar_width_hidden: 'w-zero' }, component: () => import('@/components/pages/Pricing.vue') },
    // Витрина дизайн-системы — layout + страницы из @martyrs/src/design-system/configurator.
    // Чистый холст: свой сайдбар, без overlay-хедера/футера лендинга. meta наследуется детьми.
    {
      path: 'design-system',
      meta: { title: { ru: 'Дизайн-система' }, header: null, footer: null, sidebar_width_hidden: 'w-zero', sidebar_navigation: null },
      component: () => import('@martyrs/src/design-system/configurator/components/layouts/Configurator.vue'),
      children: configuratorRoutes,
    },
    // Справочник дизайн-правил — намайнен из диалогов, чистый холст (без лендинг-хрома).
    // Через эту страницу прогоняется сгенерированная вёрстка перед сдачей.
    {
      path: 'design-rules',
      name: 'Design Rules',
      meta: { title: { ru: 'Дизайн-правила' }, header: null, footer: null, sidebar_width_hidden: 'w-zero', sidebar_navigation: null },
      component: () => import('@/design-system/DesignRules.vue'),
    },
    {
      path: 'faq',
      component: () => import('@/components/pages/faq/FAQLayout.vue'),
      children: [
        {
          path: '',
          name: 'FAQ',
          meta: { title: { ru: 'Вопросы и ответы' }, sidebar_width_hidden: 'w-zero' },
          component: () => import('@/components/pages/faq/FAQIndex.vue'),
        },
        {
          path: ':category',
          name: 'FAQ Category',
          meta: { title: { ru: 'Раздел FAQ' }, sidebar_width_hidden: 'w-zero' },
          component: () => import('@/components/pages/faq/FAQCategory.vue'),
        },
        {
          path: ':category/:article',
          name: 'FAQ Article',
          meta: { title: { ru: 'Статья FAQ' }, sidebar_width_hidden: 'w-zero' },
          component: () => import('@/components/pages/faq/FAQArticle.vue'),
        }
      ]
    },
    { path: 'links',                   name: 'Useful Links',             meta: { title: { ru: 'Полезные ссылки' }, sidebar_width_hidden: 'w-zero' }, component: () => import('@/components/pages/UsefulLinks.vue') },
    { path: 'partners',                name: 'Partners',                 meta: { title: { ru: 'Партнерам' }, sidebar_width_hidden: 'w-zero' }, component: () => import('@/components/pages/Soon.vue') },
    {
      path: 'legal/cookie',
      name: 'Cookie Policy',
      meta: {
        title: { ru: 'Использование cookie' },
        sidebar_width_hidden: 'w-zero',
      },
      component: () => import('@/components/pages/legal/CookiePolicy.vue'),
    },
    {
      path: 'legal/offer',
      name: 'Offer',
      meta: {
        title: { ru: 'Оферта на оказание услуг' },
        sidebar_width_hidden: 'w-zero',
      },
      component: () => import('@/components/pages/legal/Offer.vue'),
    },
    {
      path: 'legal/privacy-policy',
      name: 'Privacy Policy',
      meta: {
        title: { ru: 'Политика конфиденциальности' },
        sidebar_width_hidden: 'w-zero',
      },
      component: () => import('@/components/pages/legal/PrivacyPolicy.vue'),
    },
    {
      path: 'legal/personal-data',
      name: 'Personal Data',
      meta: {
        title: { ru: 'Обработка персональных данных' },
        sidebar_width_hidden: 'w-zero',
      },
      component: () => import('@/components/pages/legal/PersonalData.vue'),
    },
    {
      path: 'dashboard',
      name: 'Dashboard',
      meta: {
        title: { ru: 'Дашборд' },
        requiresAuth: true,
        header_class: "br-b-1px br-light",
        // Колокольчик уведомлений из martyrs Header'а отключён — пока не используем.
        // Меню профиля НЕ показываем — у нас собственная страница /dashboard/profile,
        // куда пользователь попадает через Sidebar.
        header_show_notifications: false,
        header_show_profile: false,
        // Снимаем лендинг-меты, унаследованные от Home: dashboard свой сайдбар
        // и без overlay-хедера / без landing-навигации.
        sidebar_width_hidden: null,
        header_mode: null,
        header_navigation: null,
        header_navigation_items: null,
        sidebar_navigation: Navigation,
        sidebar_navigation_items: sidebarNavigationItems,
        sidebar_footer_component: SidebarSupport,
        sidebarOpenOnEnter: true,
        sidebarCloseOnLeave: true
      },
      component: () => import('@/components/pages/Dashboard.vue'),
      children: [
        {
          path: '',
          name: 'Dashboard Overview',
          meta: {
            title: { ru: 'Дашборд' },
          },
          component: () => import('@/components/pages/DashboardOverview.vue'),
        },
        {
          // Онбординг после регистрации — дочерний роут dashboard, поэтому
          // наследует его requiresAuth и SSR-обработку авторизации (без вспышки).
          // Полноэкранный мастер: прячем сайдбар, чтобы ничего не отвлекало.
          path: 'get-started',
          name: 'Get Started',
          meta: {
            title: { ru: 'Начало работы' },
            sidebar_width_hidden: 'w-zero',
            sidebar_navigation: null,
            sidebar_navigation_items: null,
            sidebarOpenOnEnter: false,
          },
          component: () => import('@/components/pages/GetStarted.vue'),
        },
        {
          path: 'tasks',
          name: 'Tasks',
          meta: {
            title: { ru: 'Задачи' },
          },
          component: () => import('@/components/pages/Tasks.vue'),
        },
        {
          path: 'statistics',
          name: 'Statistics',
          meta: {
            title: { ru: 'Статистика' },
          },
          component: () => import('@/components/pages/Statistics.vue'),
        },
        // Профиль обслуживается martyrs auth-роутером (`/profile`, `/profile/edit/*`, `/profile/dashboard`)
        // — см. app/martyrs/src/modules/auth/views/router/users.router.js → getProfileRoutes().
        // Хост-роута `/dashboard/profile` больше нет; sidebar линкует на `/profile`.
        {
          path: 'jobs',
          name: 'Jobs Activity',
          meta: {
            title: { ru: 'Лента джобов' },
          },
          component: () => import('@/components/pages/JobsActivity.vue'),
        },
        {
          path: 'proxies',
          name: 'Proxies',
          meta: {
            title: { ru: 'Прокси' },
          },
          component: () => import('@/components/pages/Proxies.vue'),
        },
        {
          path: 'tasks/:id',
          name: 'Task Detail',
          meta: {
            title: { ru: 'Детали задачи' },
          },
          component: () => import('@/components/pages/TaskDetail.vue'),
        },
        {
          path: 'subscription',
          name: 'Subscription',
          meta: {
            title: { ru: 'Подписка' },
          },
          component: () => import('@/components/pages/Subscription.vue'),
        },
        {
          path: 'accounts',
          name: 'Accounts',
          meta: {
            title: { ru: 'Telegram-аккаунты' },
          },
          component: () => import('@/components/pages/Accounts.vue'),
        },
        // 301-редиректы со старых HH-путей на новые Telegram-маршруты (SEO-safe).
        { path: 'searches', redirect: { name: 'Tasks' } },
        { path: 'search/:id', redirect: to => ({ name: 'Task Detail', params: { id: to.params.id } }) },
        { path: 'applications', redirect: { name: 'Accounts' } },
        { path: 'resumes', redirect: { name: 'Proxies' } },
        { path: 'activity', redirect: { name: 'Jobs Activity' } },
      ]
    },
  ]
}];

export function getRouter() {
  const router = createRouter({
    routes,
    history,
    scrollBehavior
  });

  if (!isServer) {
    router.beforeEach((to, from, next) => {
      const requiresAuth = to.matched.some(r => r.meta.requiresAuth);
      // Источник правды — martyrs auth store. status выставляется после успешного
      // auth.actions.initialize (cookie 'user' + check-token) или login(). false —
      // сессия отсутствует / истекла, юзеру нужен заход через /signin.
      const isAuth = !!storeAuth.state.access.status;
      if (requiresAuth && !isAuth) {
        next('/auth/signin');
      } else if (to.name === 'Sign In' && isAuth) {
        next('/dashboard');
      } else {
        next();
      }
    });

    // page_view на каждый переход. Раньше скип `from.fullPath === to.fullPath` глушил
    // первую навигацию (Vue Router v4 на initial nav ставит from.fullPath='/' если
    // юзер заходит на «/»), из-за чего лендинг-визиты не отправляли trackers
    // (clientId/yclid) на бэк и Visitor.trackers оставался пустым → server-MP
    // конверсий не атрибутировал. Скипаем теперь только когда есть реальный from.name
    // (значит это уже не initial nav, а внутри-SPA повторный hit).
    router.afterEach((to, from) => {
      if (from?.name && from.fullPath === to.fullPath) return;
      useAnalytics().pageView(to.fullPath, to.meta?.title);
    });
  }

  return router;
}
